"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.lockedReqVersion = lockedReqVersion;
exports.createReq = createReq;
exports.amendReq = amendReq;
exports.finalizeReq = finalizeReq;
const path = __importStar(require("path"));
const fsExtra = __importStar(require("fs-extra"));
const dist_1 = require("../../core/dist");
const resolveThreadFolder_1 = require("./utils/resolveThreadFolder");
const createPlan_1 = require("./createPlan");
/** Minimal starter body: the three lists, ready for curation. */
const DEFAULT_REQ_BODY = [
    '### ✅ Included',
    '',
    '### ❌ Excluded',
    '',
    '### ⛓ Constraints',
    '',
].join('\n');
function reqPathFor(loomRoot, weaveSlug, threadSlug) {
    return path.join(loomRoot, 'loom', weaveSlug, threadSlug, 'req.md');
}
/**
 * The version of a thread's req when it is **locked**, else undefined.
 * Downstream doc creators stamp this onto `req_version` so re-locking the req
 * (version+1) marks the doc stale.
 */
async function lockedReqVersion(loomRoot, weaveSlug, threadSlug, deps) {
    const filePath = reqPathFor(loomRoot, weaveSlug, threadSlug);
    if (!(await deps.fs.pathExists(filePath)))
        return undefined;
    const req = (await deps.loadDoc(filePath));
    return req.status === 'locked' ? req.version : undefined;
}
async function createReq(input, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    // Resolve the thread by its stable ULID → folder (never fabricates). Path
    // helpers below stay slug-based; resolution lives here at the boundary.
    const { threadSlug, threadPath } = await (0, resolveThreadFolder_1.resolveThreadFolder)(input.weaveSlug, input.threadUlid, deps);
    const filePath = reqPathFor(loomRoot, input.weaveSlug, threadSlug);
    if (await deps.fs.pathExists(filePath)) {
        throw new Error(`A req doc already exists for ${input.weaveSlug}/${threadSlug}. Use amendReq to update it.`);
    }
    // Parent = the thread's DESIGN when present — a req is authored after a complete
    // design, so req depends on design and stamps the design version as its staleness
    // baseline. Fall back to the idea (req created before a design exists), then null.
    let parentId = null;
    let title = input.title ?? `${threadSlug} Requirements`;
    let designVersion;
    const designPath = [path.join(threadPath, 'design.md')].find(p => fsExtra.existsSync(p));
    const ideaPath = [path.join(threadPath, 'idea.md')].find(p => fsExtra.existsSync(p));
    if (designPath) {
        const design = (await deps.loadDoc(designPath));
        parentId = design.id;
        designVersion = design.version;
        if (!input.title)
            title = `${design.title} — Requirements`;
    }
    else if (ideaPath) {
        const idea = (await deps.loadDoc(ideaPath));
        parentId = idea.id;
        if (!input.title)
            title = `${idea.title} — Requirements`;
    }
    const id = (0, dist_1.generateDocId)('req');
    const frontmatter = (0, dist_1.createBaseFrontmatter)('req', id, title, parentId);
    const doc = {
        ...frontmatter,
        status: 'draft',
        content: input.content ?? DEFAULT_REQ_BODY,
        ...(designVersion !== undefined ? { design_version: designVersion } : {}),
    };
    await deps.saveDoc(doc, filePath);
    return { id, filePath };
}
/**
 * Amend a thread's req: reconcile a new body into the spec under **append-only**
 * rules, then re-open (locked → draft) and bump version (→ downstream stale).
 *
 * Requirement handles (`IN`/`EX`/`C`) are citation targets — plan steps point at
 * them via `satisfies` — so the use-case refuses any body that deletes or
 * renumbers an existing handle. New handles may be appended; an obsolete one is
 * retired by marking it `~dropped` (which keeps the handle present), never by
 * removing it. A pure re-open (no `content`) skips the check.
 */
async function amendReq(input, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    const { threadSlug, threadPath } = await (0, resolveThreadFolder_1.resolveThreadFolder)(input.weaveSlug, input.threadUlid, deps);
    const filePath = reqPathFor(loomRoot, input.weaveSlug, threadSlug);
    if (!(await deps.fs.pathExists(filePath))) {
        throw new Error(`No req doc for ${input.weaveSlug}/${threadSlug}. Use createReq first.`);
    }
    const req = (await deps.loadDoc(filePath));
    // Referential integrity: an incoming body may add handles or retire them
    // (~dropped), but must never renumber or delete an existing one.
    if (input.content !== undefined) {
        const diff = (0, dist_1.diffReqHandles)((0, dist_1.parseReq)(req.content ?? ''), (0, dist_1.parseReq)(input.content));
        if (!diff.ok) {
            throw new Error(`Amend refused: requirement handles are immutable, but this body drops ${diff.deleted.join(', ')}. ` +
                `Append new handles instead, and retire an obsolete one by marking it \`~dropped\` (do not delete or renumber).`);
        }
    }
    // An amend reconciles the req against the (possibly updated) design, so re-stamp
    // the design_version baseline — this clears the req's own design-staleness.
    const design = await (0, createPlan_1.parentDesignVersion)(threadPath, threadSlug, { loadDoc: deps.loadDoc, fs: deps.fs });
    const updated = {
        ...req,
        content: input.content ?? req.content,
        status: 'draft', // re-open for curation; a locked req drops back to draft
        version: req.version + 1, // bump → marks downstream stale
        updated: (0, dist_1.today)(),
        ...(design ? { design_version: design.version } : {}),
    };
    await deps.saveDoc(updated, filePath);
    return { id: req.id, filePath, version: updated.version };
}
async function finalizeReq(input, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    const { threadSlug } = await (0, resolveThreadFolder_1.resolveThreadFolder)(input.weaveSlug, input.threadUlid, deps);
    const filePath = reqPathFor(loomRoot, input.weaveSlug, threadSlug);
    if (!(await deps.fs.pathExists(filePath))) {
        throw new Error(`No req doc for ${input.weaveSlug}/${threadSlug}.`);
    }
    const req = (await deps.loadDoc(filePath));
    if (req.status === 'locked') {
        return { id: req.id, filePath, status: 'locked' }; // idempotent
    }
    const updated = {
        ...req,
        status: 'locked',
        updated: (0, dist_1.today)(),
    };
    await deps.saveDoc(updated, filePath);
    return { id: req.id, filePath, status: 'locked' };
}
